import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../utils/tpoGenQueryKeys';
import { useMemo } from 'react';
import { useTpoGenConfig } from '../../../context/CustomerConfigContext';
import { buildEventFormValues } from '../../../utils/buildEventPayload';

interface PromoEntry {
    promo_id: string;
    promo_name: string;
}

interface SelectedAttributeNested {
    value: string;
    dim_id: number;
    shipper_stock: string | null;
    open_stock: string | null;
    promo_names: string[];
    promos: PromoEntry[];
}

export interface EventDataResponse {
    event_id: string;
    plan_id: string;
    event_name: string;
    event_type?: string;
    start_date: string;
    end_date: string;
    display_acv: number;
    feature_acv: number;
    created_by: string | null;
    event_status: string;
    selected_attribute: string[];
    selected_attribute_nested: SelectedAttributeNested[];
    brand_abbreviation: [string, string][];
    created_at: string;
    updated_at: string;
    attributes?: Record<string, unknown>;
}

const fetchEventById = async (eventId: string, signal?: AbortSignal) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.getEventById(eventId), {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });
    return response.data as EventDataResponse;
};

const useGetEventData = (eventId: string | null) => {
    const { eventDetailsFields, entityAttributes } = useTpoGenConfig();
    const { data, isPending, isFetching, isError } = useQuery({
        queryKey: tpoGenKeys.eventDetails(eventId ?? ''),
        queryFn: ({ signal }) => fetchEventById(eventId as string, signal),
        enabled: !!eventId,
    });

    const formValues = useMemo(() => {
        if (!data) return null;
        return {
            ...buildEventFormValues(entityAttributes, eventDetailsFields, data as unknown as Record<string, unknown>),
            'dim1-brand': data.selected_attribute ?? [],
            'dim1-material_group': data.selected_attribute_nested?.map((item) => item.dim_id) ?? [],
        };
    }, [data, eventDetailsFields, entityAttributes]);

    const eventPromos = useMemo<PromoEntry[]>(() => {
        if (!data?.selected_attribute_nested) return [];
        const seen = new Set<string>();
        const unique: PromoEntry[] = [];
        for (const attr of data.selected_attribute_nested) {
            for (const promo of attr.promos ?? []) {
                if (!seen.has(promo.promo_id)) {
                    seen.add(promo.promo_id);
                    unique.push(promo);
                }
            }
        }
        return unique;
    }, [data]);

    return {
        formValues,
        eventData: data ?? null,
        eventPromos,
        isPending: !!eventId && isPending,
        isFetching,
        isError,
    };
};

export default useGetEventData;
